import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, NavLink as RouterLink, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

import home from '../../../images/Nav-Hub.svg'
import features from '../../../images/Nav-Features.svg'
import pledges from '../../../images/Nav-Pledges.svg'
import setting from '../../../images/Nav-Settings.svg'
import reminders from '../../../images/Nav-Reminders.svg'
import school from '../../../images/Nav-Students.svg'
import queries from '../../../images/Nav-Queries.svg'
import chat from '../../../images/future-nav/Nav-Chat.svg'
import message from '../../../images/future-nav/Nav-Message.svg'
import download from '../../../images/future-nav/Nav-Download.svg'
import info from '../../../images/future-nav/Nav-Info.svg'
import chevron from '../../../images/future-nav/Nav-Down.svg'
import classes from './MainSidebar.module.css'
import own from './FutureSidebarNav.module.css'
import { FUTURE_NAV, filterNav, linkHref, sectionHref, type NavEntry, type NavLink, type NavSection } from './futureNav'

/** The prototype's icon for each entry (same Iconly set the website already uses). */
const ICONS: Record<string, string> = {
    home,
    buki: chat,
    todo: reminders,
    hub: features,
    fundraising: pledges,
    school,
    forms: queries,
    communication: message,
    reports: queries,
    import: download,
    tools: features,
    settings: setting,
    help: info,
}

type AuthSlice = { auth: { permissions?: { VisibleTabs?: string[] }; hub?: boolean } }

const isAt = (pathname: string, link: NavLink) => pathname === linkHref(link) || (!link.soon && !!link.path && pathname.startsWith(`${link.path}/`))
const sectionAt = (pathname: string, s: NavSection) => s.items.some((l) => isAt(pathname, l))
/** The section's line or one of the links nested under it is this page (opens its group). */
const sectionOrSubAt = (pathname: string, s: NavSection) => sectionAt(pathname, s) || (s.sub ?? []).some((l) => isAt(pathname, l))

const SoonTag = () => (
    <span className={own.soon} data-testid="sidebar-soon-tag">
        Soon
    </span>
)

const FutureSidebarNav = ({ onClose }: { onClose?: () => void }) => {
    const visibleTabs = useSelector((s: AuthSlice) => s.auth.permissions?.VisibleTabs)
    const allowHub = useSelector((s: AuthSlice) => !!s.auth.hub)
    const { pathname } = useLocation()

    const nav = useMemo(() => filterNav(FUTURE_NAV, { visibleTabs, allowHub }), [visibleTabs, allowHub])

    const [open, setOpen] = useState<Record<string, boolean>>({})
    const [showMore, setShowMore] = useState<Record<string, boolean>>({})

    // A group opens by itself when the page you are on belongs to it (a Soon link's coming-soon
    // page counts too, so clicking one doesn't fold the menu away).
    useEffect(() => {
        const hit = nav.find((e) => (e.sections ?? []).some((s) => sectionOrSubAt(pathname, s)))
        if (hit) setOpen((o) => (o[hit.id] ? o : { ...o, [hit.id]: true }))
    }, [nav, pathname])

    const close = () => onClose?.()

    const renderTop = (entry: NavEntry, link: NavLink) => (
        <li key={entry.id} className={classes['main-sidebar__item']}>
            <RouterLink
                to={linkHref(link)}
                data-testid={`sidebar-link-${link.id}`}
                onClick={close}
                className={({ isActive }) => `${classes['main-sidebar__nav']} ${own.top} ${isActive ? classes.active : ''}`}
            >
                <div className={classes['nav-icon-wrapper']}>
                    <img className={classes['nav-icon']} src={ICONS[entry.icon]} alt="" />
                </div>
                <label>{link.label}</label>
                {link.soon && <SoonTag />}
            </RouterLink>
        </li>
    )

    const renderSection = (s: NavSection) => {
        const current = sectionAt(pathname, s)
        const outside = s.items[0].href
        if (outside)
            return (
                <li key={s.id} className={own.subitem}>
                    <a href={outside} target="_blank" rel="noopener noreferrer" data-testid={`sidebar-section-${s.id}`} onClick={close} className={own.sublink}>
                        <span className={own.sublinkLabel}>{s.label}</span>
                    </a>
                </li>
            )
        return (
            <Fragment key={s.id}>
                <li className={own.subitem}>
                    {/* A plain Link: NavLink would overwrite aria-current with a check that only knows its first link. */}
                    <Link
                        to={sectionHref(s)}
                        data-testid={`sidebar-section-${s.id}`}
                        onClick={close}
                        aria-current={current ? 'page' : undefined}
                        className={`${own.sublink} ${current ? own.sublinkActive : ''}`}
                    >
                        <span className={own.sublinkLabel}>{s.label}</span>
                        {s.items.every((l) => l.soon) && <SoonTag />}
                    </Link>
                </li>
                {(s.sub ?? []).map(renderNested)}
            </Fragment>
        )
    }

    /** A screen nested under a section line (Donors › Occasions), drawn one step further in. */
    const renderNested = (link: NavLink) => {
        const current = isAt(pathname, link)
        return (
            <li key={link.id} className={own.subitem}>
                <Link
                    to={linkHref(link)}
                    data-testid={`sidebar-sublink-${link.id}`}
                    onClick={close}
                    aria-current={current ? 'page' : undefined}
                    className={`${own.sublink} ${own.nested} ${current ? own.sublinkActive : ''}`}
                >
                    <span className={own.sublinkLabel}>{link.label}</span>
                    {link.soon && <SoonTag />}
                </Link>
            </li>
        )
    }

    return (
        <Fragment>
            {nav.map((entry) => {
                if (entry.link) return renderTop(entry, entry.link)

                const sections = entry.sections ?? []
                const isOpen = !!open[entry.id]
                const hasActive = sections.some((s) => sectionOrSubAt(pathname, s))
                const main = sections.filter((s) => !s.more)
                const extra = sections.filter((s) => s.more)
                const moreOpen = showMore[entry.id] ?? extra.some((s) => sectionAt(pathname, s))

                return (
                    <Fragment key={entry.id}>
                        <li className={classes['main-sidebar__item']}>
                            {/* The whole row opens and closes the group, as on the existing website. */}
                            <button
                                type="button"
                                data-testid={`sidebar-group-${entry.id}`}
                                aria-expanded={isOpen}
                                onClick={() => setOpen((o) => ({ ...o, [entry.id]: !o[entry.id] }))}
                                className={`${classes['main-sidebar__nav']} ${own.top} ${own.toggle} ${hasActive ? classes['active-parent'] : ''} ${isOpen ? own.toggleOpen : ''}`}
                            >
                                <div className={classes['nav-icon-wrapper']}>
                                    <img className={classes['nav-icon']} src={ICONS[entry.icon]} alt="" />
                                </div>
                                <label>{entry.label}</label>
                                <img className={`${own.chev} ${isOpen ? own.chevOpen : ''}`} src={chevron} alt="" />
                            </button>
                        </li>
                        {isOpen && (
                            <ul className={own.submenu}>
                                {main.map(renderSection)}
                                {moreOpen && extra.map(renderSection)}
                                {extra.length > 0 && (
                                    <li className={own.subitem}>
                                        <button
                                            type="button"
                                            data-testid={`sidebar-more-${entry.id}`}
                                            aria-expanded={moreOpen}
                                            onClick={() => setShowMore((m) => ({ ...m, [entry.id]: !moreOpen }))}
                                            className={`${own.sublink} ${own.more}`}
                                        >
                                            <img className={`${own.moreChev} ${moreOpen ? own.chevOpen : ''}`} src={chevron} alt="" />
                                            <span className={own.sublinkLabel}>{moreOpen ? 'Show less' : 'Show more'}</span>
                                        </button>
                                    </li>
                                )}
                            </ul>
                        )}
                    </Fragment>
                )
            })}
        </Fragment>
    )
}

export default FutureSidebarNav
